import { Box, Tooltip, Typography } from "@mui/material";
import type { EChartsOption } from "echarts";
import ReactECharts from "echarts-for-react";
import { useRef } from "react";
import type {
	InspectionDashboardDefect,
	InspectionDashboardDefectTrendPoint,
	InspectionDashboardGrain,
} from "../../../models/InspectionDashboard";
import { formatCount, formatPercent, rate } from "../../utils/dashboardFormat";
import { DASHBOARD_INK } from "../../utils/dashboardTokens";
import { DefectTrendChart } from "./DefectTrendChart";

const TOP_DEFECTS = 5;

type SlicePart = {
	name: string;
	value: number;
};

type Slice = {
	name: string;
	value: number;
	color: string;
	parts?: SlicePart[];
};

export const buildDefectSlices = (
	defects: InspectionDashboardDefect[],
): Slice[] => {
	const top = defects.slice(0, TOP_DEFECTS).map((defect) => ({
		name: defect.SurfaceDefectName,
		value: defect.DefectCount,
		color: defect.Color || DASHBOARD_INK.AXIS,
	}));

	const rest = defects.slice(TOP_DEFECTS);
	if (rest.length === 0) return top;

	return [
		...top,
		{
			name: `Other (${rest.length} ${rest.length === 1 ? "type" : "types"})`,
			value: rest.reduce((total, defect) => total + defect.DefectCount, 0),
			color: "#6f6e69",
			parts: rest.map((defect) => ({
				name: defect.SurfaceDefectName,
				value: defect.DefectCount,
			})),
		},
	];
};
const countNoun = (value: number, isGroup: boolean) =>
	isGroup
		? value === 1
			? "defect"
			: "defects"
		: value === 1
			? "reading"
			: "readings";

export const DefectMixChart = ({
	defects,
	trend,
	grain,
	isEvent,
}: {
	defects: InspectionDashboardDefect[];
	trend: InspectionDashboardDefectTrendPoint[];
	grain: InspectionDashboardGrain;
	isEvent: boolean;
}) => {
	const ringRef = useRef<ReactECharts>(null);
	const trendRef = useRef<ReactECharts>(null);
	const legendRef = useRef<HTMLDivElement>(null);

	const slices = buildDefectSlices(defects);
	const total = slices.reduce((sum, slice) => sum + slice.value, 0);

	const markLegend = (sliceIndex: number | null) => {
		const legend = legendRef.current;
		if (!legend) return;

		legend.dataset.highlighting = String(sliceIndex !== null);
		legend.querySelectorAll<HTMLElement>("[data-slice]").forEach((row) => {
			row.dataset.active = String(Number(row.dataset.slice) === sliceIndex);
		});
	};

	const highlight = (sliceIndex: number) => {
		if (!slices[sliceIndex]) return;

		ringRef.current?.getEchartsInstance().dispatchAction({
			type: "highlight",
			seriesIndex: 0,
			dataIndex: sliceIndex,
		});

		if (!slices[sliceIndex].parts)
			trendRef.current?.getEchartsInstance().dispatchAction({
				type: "highlight",
				seriesName: slices[sliceIndex].name,
			});

		markLegend(sliceIndex);
	};

	const clearHighlight = () => {
		ringRef.current?.getEchartsInstance().dispatchAction({ type: "downplay" });
		trendRef.current?.getEchartsInstance().dispatchAction({ type: "downplay" });
		markLegend(null);
	};

	const sliceOfDefect = (name: string) =>
		slices.findIndex(
			(slice) =>
				slice.name === name ||
				!!slice.parts?.some((part) => part.name === name),
		);

	const ringEvents = {
		mouseover: (params: unknown) => {
			const { dataIndex } = params as { dataIndex?: number };
			if (typeof dataIndex === "number") highlight(dataIndex);
		},
		mouseout: clearHighlight,
	};

	const trendEvents = {
		mouseover: (params: unknown) => {
			const { componentType, seriesName } = params as {
				componentType?: string;
				seriesName?: string;
			};
			if (componentType !== "series" || !seriesName) return;

			const sliceIndex = sliceOfDefect(seriesName);
			if (sliceIndex >= 0) highlight(sliceIndex);
		},
		mouseout: clearHighlight,
	};

	const option: EChartsOption = {
		tooltip: {
			trigger: "item",
			appendTo: "body",
			position: (point, _params, _dom, _rect, size) => [
				point[0] + 16,
				point[1] - size.contentSize[1] / 2,
			],
			borderColor: DASHBOARD_INK.GRID,
			formatter: (parameters: unknown) => {
				const entry = parameters as { name?: string; value?: number };
				const value = entry.value ?? 0;
				const slice = slices.find((item) => item.name === entry.name);
				const parts = slice?.parts ?? [];

				const breakdown = parts
					.map(
						(part) => `<div style="display:flex;gap:12px;margin-top:2px">
						<span style="color:${DASHBOARD_INK.SECONDARY}">${part.name}</span>
						<span style="margin-left:auto;font-variant-numeric:tabular-nums">${formatCount(part.value)} ${countNoun(part.value, false)} · ${formatPercent(rate(part.value, total), 0)}</span>
					</div>`,
					)
					.join("");

				return `<div style="min-width:${parts.length > 0 ? 240 : 0}px">
					<div style="font-weight:600">${entry.name ?? ""}</div>
					<div style="color:${DASHBOARD_INK.SECONDARY};font-variant-numeric:tabular-nums">${formatCount(value)} ${countNoun(value, parts.length > 0)} · ${formatPercent(rate(value, total))} of all defects</div>
					${breakdown}
				</div>`;
			},
		},
		series: [
			{
				type: "pie",
				radius: ["58%", "82%"],
				center: ["50%", "50%"],
				avoidLabelOverlap: false,
				label: { show: false },
				labelLine: { show: false },
				itemStyle: { borderColor: "#ffffff", borderWidth: 2 },
				emphasis: { focus: "self", scaleSize: 4 },
				data: slices.map((slice) => ({
					name: slice.name,
					value: slice.value,
					itemStyle: { color: slice.color },
				})),
			},
		],
	};

	return (
		<>
			<Box
				sx={{
					alignItems: "center",
					display: "flex",
					flexWrap: "nowrap",
					gap: 2,
				}}
			>
				{trend.length > 0 && (
					<Box sx={{ flex: "1 1 0", minWidth: 0 }}>
						<DefectTrendChart
							chartRef={trendRef}
							onEvents={trendEvents}
							trend={trend}
							grain={grain}
							isEvent={isEvent}
							height={180}
						/>
					</Box>
				)}
				<Box
					sx={{
						position: "relative",
						flexShrink: 0,
						width: 170,
						height: 180,
					}}
				>
					<ReactECharts
						ref={ringRef}
						option={option}
						notMerge
						onEvents={ringEvents}
						style={{ height: "100%", width: "100%" }}
						opts={{ renderer: "svg" }}
					/>
					<Box
						sx={{
							alignItems: "center",
							display: "flex",
							flexDirection: "column",
							inset: 0,
							justifyContent: "center",
							pointerEvents: "none",
							position: "absolute",
						}}
					>
						<Typography
							sx={{
								color: DASHBOARD_INK.PRIMARY,
								fontSize: "1.5rem",
								fontWeight: 600,
								lineHeight: 1,
							}}
						>
							{formatCount(total)}
						</Typography>
						<Typography
							sx={{ color: DASHBOARD_INK.MUTED, fontSize: "0.75rem" }}
						>
							defects
						</Typography>
					</Box>
				</Box>
				<Box
					ref={legendRef}
					sx={{
						flex: "0 0 210px",
						minWidth: 0,
						"& [data-slice]": { transition: "opacity 120ms" },
						'&[data-highlighting="true"] [data-slice]:not([data-active="true"])':
							{ opacity: 0.4 },
					}}
				>
					{slices.map((slice, sliceIndex) => (
						<Tooltip
							key={slice.name}
							placement="left"
							title={
								slice.parts ? (
									<Box sx={{ display: "grid", gap: 0.25 }}>
										{slice.parts.map((part) => (
											<Box
												key={part.name}
												sx={{ display: "flex", gap: 2, whiteSpace: "nowrap" }}
											>
												<span>{part.name}</span>
												<Box
													component="span"
													sx={{
														fontVariantNumeric: "tabular-nums",
														marginLeft: "auto",
													}}
												>
													{formatPercent(rate(part.value, total), 0)} ·{" "}
													{formatCount(part.value)}
												</Box>
											</Box>
										))}
									</Box>
								) : (
									""
								)
							}
						>
							<Box
								data-slice={sliceIndex}
								onMouseEnter={() => highlight(sliceIndex)}
								onMouseLeave={clearHighlight}
								sx={{
									alignItems: "center",
									cursor: "default",
									display: "flex",
									gap: 1,
									py: 0.5,
								}}
							>
								<Box
									sx={{
										backgroundColor: slice.color,
										borderRadius: "3px",
										flexShrink: 0,
										height: 12,
										width: 12,
									}}
								/>
								<Typography
									noWrap
									sx={{ color: DASHBOARD_INK.PRIMARY, fontSize: "0.8125rem" }}
								>
									{slice.name}
								</Typography>
								<Box sx={{ marginLeft: "auto", pl: 1 }}>
									<Typography
										component="span"
										sx={{
											color: DASHBOARD_INK.SECONDARY,
											fontSize: "0.8125rem",
											fontVariantNumeric: "tabular-nums",
											whiteSpace: "nowrap",
											"& b": { fontWeight: 600 },
										}}
									>
										<b>{formatPercent(rate(slice.value, total), 0)}</b> ·{" "}
										{formatCount(slice.value)}
									</Typography>
								</Box>
							</Box>
						</Tooltip>
					))}
				</Box>
			</Box>
		</>
	);
};
